/* Velour header: mega-menu (hover / click / keyboard), search toggle, bag toggle.
   specs/01-global.md "Mega-menu" and "Search". Relies on trapFocus/removeTrapFocus
   from global.js. */

class VelourHeader extends HTMLElement {
  connectedCallback() {
    this.openPanel = null;
    this.closeTimer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
      const panel = this.querySelector('#MegaPanel-' + trigger.dataset.megaTrigger);
      const li = trigger.closest('.header__nav-item');
      if (!panel || !li) return;

      li.addEventListener('mouseenter', () => this.openMega(trigger, panel, li, false));
      li.addEventListener('mouseleave', () => this.scheduleClose(trigger, panel, li));

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (panel.hasAttribute('data-open')) {
          this.closeMega(trigger, panel, li);
        } else {
          this.openMega(trigger, panel, li, true);
        }
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && panel.hasAttribute('data-open')) {
          this.closeMega(trigger, panel, li);
          trigger.focus();
        }
      });

      panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeMega(trigger, panel, li);
          trigger.focus();
        }
      });
    });

    this.querySelectorAll('.header__nav-item:not(.has-mega) > .header__nav-link').forEach((link) => {
      link.addEventListener('mouseenter', () => {
        if (this.openPanel) this.closeMega(this.openPanel.trigger, this.openPanel.panel, this.openPanel.li);
      });
    });

    this.addEventListener('mouseleave', () => {
      if (this.openPanel) this.scheduleClose(this.openPanel.trigger, this.openPanel.panel, this.openPanel.li);
    });

    document.addEventListener('click', (event) => {
      if (this.openPanel && !this.contains(event.target)) {
        this.closeMega(this.openPanel.trigger, this.openPanel.panel, this.openPanel.li);
      }
    });

    this.setupSearch();
    this.setupBagToggle();
  }

  openMega(trigger, panel, li, isClick) {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    if (this.openPanel && this.openPanel.panel !== panel) {
      this.closeMega(this.openPanel.trigger, this.openPanel.panel, this.openPanel.li);
    }
    panel.removeAttribute('hidden');
    requestAnimationFrame(() => panel.setAttribute('data-open', ''));
    trigger.setAttribute('aria-expanded', 'true');
    li.classList.add('is-open');
    this.openPanel = { trigger, panel, li };
    if (isClick) trapFocus(panel, panel.querySelector('a, button'));
  }

  scheduleClose(trigger, panel, li) {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closeMega(trigger, panel, li), 250);
  }

  closeMega(trigger, panel, li) {
    panel.removeAttribute('data-open');
    trigger.setAttribute('aria-expanded', 'false');
    li.classList.remove('is-open');
    if (this.openPanel && this.openPanel.panel === panel) {
      removeTrapFocus();
      this.openPanel = null;
    }
    const delay = this.reducedMotion ? 0 : 320;
    setTimeout(() => {
      if (!panel.hasAttribute('data-open')) panel.setAttribute('hidden', '');
    }, delay);
  }

  setupSearch() {
    const toggle = this.querySelector('[data-search-toggle]');
    const overlay = this.querySelector('[data-search-overlay]');
    const closeBtn = this.querySelector('[data-search-close]');
    const input = this.querySelector('[data-search-input]');
    if (!toggle || !overlay) return;

    const close = () => {
      overlay.removeAttribute('data-open');
      toggle.setAttribute('aria-expanded', 'false');
      removeTrapFocus(toggle);
      setTimeout(() => overlay.setAttribute('hidden', ''), this.reducedMotion ? 0 : 320);
    };

    toggle.addEventListener('click', () => {
      if (overlay.hasAttribute('data-open')) {
        close();
        return;
      }
      overlay.removeAttribute('hidden');
      requestAnimationFrame(() => overlay.setAttribute('data-open', ''));
      toggle.setAttribute('aria-expanded', 'true');
      trapFocus(overlay, input);
    });

    closeBtn?.addEventListener('click', close);
    overlay.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') close();
    });
  }

  setupBagToggle() {
    const cartToggle = this.querySelector('[data-cart-toggle]');
    if (!cartToggle) return;
    cartToggle.addEventListener('click', (event) => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open();
      }
    });
  }
}

customElements.define('velour-header', VelourHeader);

/* Mobile menu drawer.
   global.js's HeaderDrawer was written for Dawn's header markup (.section-header,
   .header-wrapper), which this theme does not have, so its open and close methods threw
   an error. These replacements do the same job without those elements, and the backdrop
   and close button in snippets/header-drawer.liquid close the drawer through them. */
customElements.whenDefined('header-drawer').then(() => {
  const HeaderDrawerClass = customElements.get('header-drawer');
  const MenuDrawerClass = customElements.get('menu-drawer');
  if (!HeaderDrawerClass || !MenuDrawerClass) return;

  HeaderDrawerClass.prototype.openMenuDrawer = function (summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add('overflow-hidden-' + this.dataset.breakpoint);
  };

  HeaderDrawerClass.prototype.closeMenuDrawer = function (event, elementToFocus) {
    if (!event) return;
    const summary = elementToFocus || this.mainDetailsToggle.querySelector('summary');
    MenuDrawerClass.prototype.closeMenuDrawer.call(this, event, summary);
    summary.setAttribute('aria-expanded', false);
  };
});

document.addEventListener('click', (event) => {
  const closer = event.target.closest('[data-menu-close]');
  if (!closer) return;
  const drawer = closer.closest('header-drawer');
  if (drawer && typeof drawer.closeMenuDrawer === 'function') drawer.closeMenuDrawer(event);
});
